"use client";

import { ChangeEvent, useCallback, useEffect, useReducer } from "react";
import Image from "next/image";
import { FileVideo, Loader2, Search, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

import { useStudio } from "@/lib/state/studio";
import { trpc } from "@/lib/trpc/client";

export interface LibraryVideo {
  id: string;
  title: string;
  filename: string;
  thumbnailUrl: string | null;
  duration: number | null;
  createdAt: string;
  /**
   * Set when the video is attached to one or more posts. `title` is the most
   * recent post's title; `moreCount` is how many additional posts use it.
   */
  usedIn?: { title: string; moreCount: number } | null;
}

export function getDisplayTitle(video: {
  title: string;
  filename: string;
}): string {
  // Mux asset IDs are lowercase alphanumeric and land in the 20-30 char range.
  // Real filenames have dots / uppercase / lengths outside that range.
  return /^[a-z0-9]{20,30}$/.test(video.title) ? video.filename : video.title;
}

interface SelectVideoModalProps {
  onConfirmClick: (video: LibraryVideo) => void;
  isLoading: boolean;
  onClose: () => void;
}

/**
 * Presentational picker card with its own close (X) button. It does NOT provide
 * a backdrop/overlay or modal mechanics — the host supplies those (a Radix
 * Dialog in the post composer, the modal provider in the post editor). This
 * keeps focus, scroll-lock, and pointer-event handling owned by a single,
 * battle-tested modal layer rather than fighting one from a sibling portal.
 */
export default function SelectVideoModal({
  onConfirmClick,
  isLoading,
  onClose,
}: SelectVideoModalProps) {
  type State = {
    videos: LibraryVideo[];
    filteredVideos: LibraryVideo[];
    selectedId: string | null;
    isFetching: boolean;
    error: boolean;
  };
  type Action =
    | { type: "fetch_start" }
    | { type: "fetch_done"; videos: LibraryVideo[] }
    | { type: "fetch_error" }
    | { type: "filter"; query: string }
    | { type: "select"; id: string };

  const [state, dispatch] = useReducer(
    (s: State, action: Action): State => {
      switch (action.type) {
        case "fetch_start":
          return { ...s, isFetching: true, error: false };
        case "fetch_done":
          return {
            ...s,
            isFetching: false,
            error: false,
            videos: action.videos,
            filteredVideos: action.videos,
          };
        case "fetch_error":
          return { ...s, isFetching: false, error: true };
        case "filter": {
          const q = action.query.toLowerCase();
          return {
            ...s,
            filteredVideos: q
              ? s.videos.filter((v) =>
                  getDisplayTitle(v).toLowerCase().includes(q),
                )
              : s.videos,
          };
        }
        case "select":
          return { ...s, selectedId: action.id };
      }
    },
    {
      videos: [],
      filteredVideos: [],
      selectedId: null,
      isFetching: false,
      error: false,
    },
  );

  const { videos, filteredVideos, selectedId, isFetching, error } = state;
  const { studio } = useStudio();
  const studioId = studio?.id;
  const utils = trpc.useUtils();

  const loadVideos = useCallback(() => {
    if (!studioId) return;

    dispatch({ type: "fetch_start" });
    utils.client.videos.list
      .query()
      .then((data) => {
        dispatch({
          type: "fetch_done",
          videos: data as unknown as LibraryVideo[],
        });
      })
      .catch(() => dispatch({ type: "fetch_error" }));
  }, [studioId, utils]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "filter", query: e.target.value });
  }, []);

  const handleSelect = useCallback(
    (video: LibraryVideo) => {
      if (isLoading) return;
      dispatch({ type: "select", id: video.id });
      onConfirmClick(video);
    },
    [isLoading, onConfirmClick],
  );

  return (
    <div className="relative w-[28rem] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-lg">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="flex flex-col gap-4 p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Video library
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Select an existing video to attach to this lesson.
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search videos..."
            onChange={onSearch}
            className="h-9 w-full rounded-lg bg-muted pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex h-48 flex-col gap-2 overflow-y-auto overscroll-contain">
          {isFetching && (
            <div className="flex flex-1 items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isFetching && error && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
              <FileVideo className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t load your videos.
              </p>
              <button
                type="button"
                onClick={loadVideos}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Retry
              </button>
            </div>
          )}

          {!isFetching && !error && filteredVideos.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
              <FileVideo className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {videos.length === 0
                  ? "No videos uploaded yet."
                  : "No videos match your search."}
              </p>
            </div>
          )}

          {!isFetching &&
            !error &&
            filteredVideos.map((video) => {
              const isSelected = selectedId === video.id;
              const displayTitle = getDisplayTitle(video);
              const metadata = [
                video.duration != null ? formatDuration(video.duration) : null,
                formatDistanceToNow(new Date(video.createdAt), {
                  addSuffix: true,
                }),
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <button
                  key={video.id}
                  type="button"
                  disabled={isLoading}
                  onClick={() => {
                    handleSelect(video);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {video.thumbnailUrl ? (
                    <Image
                      src={video.thumbnailUrl}
                      alt={displayTitle}
                      width={80}
                      height={48}
                      className="h-12 w-20 flex-shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-20 flex-shrink-0 items-center justify-center rounded-md bg-muted">
                      <FileVideo className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {displayTitle}
                    </p>
                    {metadata && (
                      <p className="text-xs text-muted-foreground">
                        {metadata}
                      </p>
                    )}
                    {video.usedIn && (
                      <p className="mt-1 flex items-center gap-1 text-xs">
                        <span className="inline-flex shrink-0 items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Used in
                        </span>
                        <span className="min-w-0 truncate text-foreground">
                          {video.usedIn.title}
                          {video.usedIn.moreCount > 0 &&
                            ` +${video.usedIn.moreCount} more`}
                        </span>
                      </p>
                    )}
                  </div>
                  {isLoading && isSelected && (
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
                  )}
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
